import React from "react";
import { formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/button";
import { Notification } from "@/types/notification";
import { Heart, Eye } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getProfileUrl } from "@/utils/profileRedirects";

interface NotificationItemProps {
  notification: Notification;
  onActionClick?: () => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onActionClick }) => {
  const {
    type,
    actor_name,
    actor_avatar_url,
    message,
    created_at,
    read,
    actor_id,
    is_anonymous
  } = notification;

  // Fetch actor's profile data (profession, location, and username_slug) for view notifications
  const shouldFetchProfile = Boolean(
    actor_id && 
    !is_anonymous && 
    (type === "view" || type === "profile_view")
  );

  const { data: actorProfile } = useQuery({
    queryKey: ["actor-profile", actor_id],
    queryFn: async () => {
      if (!actor_id) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("profession, location, username_slug")
        .eq("id", actor_id)
        .single();
      
      if (error) {
        console.warn("Error fetching actor profile:", error);
        return null;
      }
      return data;
    },
    enabled: shouldFetchProfile,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Handle profile navigation - open in new tab with full public URL
  const handleProfileClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!actor_id) return;
    
    const profileUrl = getProfileUrl(actor_id, actorProfile?.username_slug);
    if (profileUrl) {
      window.open(profileUrl, "_blank", "noopener,noreferrer");
    }
  };

  // Check if profile is clickable (known viewer)
  const isProfileClickable = Boolean(actor_id && !is_anonymous);

  // Generate initials for the avatar fallback
  const getInitials = (name: string | null | undefined) => {
    if (!name) return 'AN'; // Anonymous
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  // Capitalize location (title case)
  const capitalizeLocation = (location: string | null | undefined) => {
    if (!location) return null;
    return location
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Determine action button based on notification type
  const getActionButton = () => {
    switch (type) {
      case "portfolio_request":
        return (
          <Button
            variant="ghost"
            size="sm"
            className="text-xs mt-1 text-primary h-8 border border-gray-200"
            onClick={onActionClick}
          >
            Update Portfolio
          </Button>
        );
      case "experience_request":
        return (
          <Button
            variant="ghost"
            size="sm"
            className="text-xs mt-1 text-primary h-8 border border-gray-200"
            onClick={onActionClick}
          >
            Add Experience
          </Button>
        );
      case "like":
      case "view":
      case "profile_view":
        // No button for view notifications - profile photo and name are clickable instead
        return null;
      default:
        return null;
    }
  };

  return (
    <TooltipProvider>
      <div 
        className={`p-4 ${!read ? "bg-blue-50 dark:bg-blue-900/20" : ""}`}
      >
        <div className="flex items-start gap-3">
          <Tooltip>
            <TooltipTrigger asChild>
              <div 
                className={`relative h-10 w-10 ${isProfileClickable ? "cursor-pointer" : ""}`}
                onClick={isProfileClickable ? handleProfileClick : undefined}
              >
                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                  {actor_avatar_url ? (
                    <img
                      src={actor_avatar_url}
                      alt={actor_name || "User"}
                      className="h-10 w-10 rounded-full"
                    />
                  ) : (
                    <span className="text-sm font-medium">
                      {actor_name ? getInitials(actor_name) : 'AN'}
                    </span>
                  )}
                </div>

                {/* Notification type icon badge */}
                {(type === "like" || type === "profile_like") && (
                  <div className="absolute -top-0.5 -right-0.5 h-5 w-5 rounded-full bg-white flex items-center justify-center border-[.5px] border-gray-200">
                    <Heart className="h-3 w-3 text-red-500" />
                  </div>
                )}
                {(type === "view" || type === "profile_view") && (
                  <div className="absolute -top-0.5 -right-0.5 h-5 w-5 rounded-full bg-white flex items-center justify-center border-[.5px] border-gray-200">
                    <Eye className="h-3 w-3 text-orange-500" />
                  </div>
                )}
              </div>
            </TooltipTrigger>
            {isProfileClickable && (
              <TooltipContent>
                <p>View Profile</p>
              </TooltipContent>
            )}
          </Tooltip>
          
          <div className="flex-1">
            <div className="flex flex-col md:flex-row md:justify-between">
              <p className="text-sm font-medium">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span 
                      className={isProfileClickable ? "cursor-pointer hover:underline" : ""}
                      onClick={isProfileClickable ? handleProfileClick : undefined}
                    >
                      {actor_name ? actor_name : "Anonymous"}
                    </span>
                  </TooltipTrigger>
                  {isProfileClickable && (
                    <TooltipContent>
                      <p>View Profile</p>
                    </TooltipContent>
                  )}
                </Tooltip>
                {" "}
                <span className="text-gray-500 font-normal">
                  {getActionText(type)}
                </span>
              </p>
              <span className="text-xs text-gray-500">
                {formatDistanceToNow(new Date(created_at), { addSuffix: true })}
              </span>
            </div>
            
            {/* Only show message for anonymous viewers or non-view notifications */}
            {message && !shouldFetchProfile && (
              <p className="text-sm text-gray-600 mt-1">{message}</p>
            )}
            
            {/* Display role and location for view notifications when viewer is known */}
            {shouldFetchProfile && actorProfile && (
              <p className="text-sm text-gray-600 mt-1">
                {[
                  actorProfile.profession,
                  capitalizeLocation(actorProfile.location)
                ]
                  .filter(Boolean)
                  .join(" | ")}
              </p>
            )}
            
            {getActionButton()}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

// Helper function to get action text based on notification type
const getActionText = (type: string): string => {
  switch (type) {
    case "like":
    case "profile_like":
      return "liked your profile";
    case "view":
    case "profile_view":
      return "viewed your profile";
    case "message":
      return "sent you a message";
    case "request":
      return "sent you a request";
    case "follow":
    case "new_follower":
      return "started following you";
    case "comment":
      return "commented on your profile";
    case "mention":
      return "mentioned you";
    case "portfolio_request":
      return "requested to see your portfolio";
    case "experience_request":
      return "requested to see your experience";
    case "about_request":
      return "requested to learn more about you";
    case "portfolio_reminder":
      return "is interested in your portfolio";
    case "experience_reminder":
      return "is interested in your experience";
    case "system":
      return "system notification";
    default:
      return `sent a ${type} notification`;
  }
};

export default NotificationItem;
